import useNotification from "../../hooks/useNotification";
import apiService from "../../service/api-service";

/**
 * Options for downloading a DOCX file.
 */
export interface DownloadDocxOptions {
    /** The API endpoint URL that returns the DOCX file */
    apiUrl: string;
    /** Optional query parameters to append to the URL */
    params?: Record<string, string>;
    /** Optional additional headers (e.g., auth or subscription keys) */
    headers?: Record<string, string>;
    filename?: string;
  }
  
  /**
   * Downloads a DOCX file using the provided apiService.
   *
   * Ensure that `apiService` is properly imported and configured in your project.
   *
   * @param options - The download options including apiUrl, params, and headers.
   * @throws Will throw an error if the download fails.
   */
  export async function downloadDocx(options: DownloadDocxOptions): Promise<void> {
    const { apiUrl, params, headers, filename: customFilename } = options;
    try {
      // Perform the API call using the provided apiService with the DOCX Accept header.
      const response = await apiService.get<Blob>(apiUrl, params, {
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          ...headers,
        },
        responseType: 'blob'
      });
  
      // Extract the Blob from the ApiResponse.
      const blob = response.data;
  
      // Create a blob URL for the downloaded data.
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
  
      let filename = customFilename || 'download.docx';
  
      // Attempt to extract the filename from the Content-Disposition header only if custom filename not provided.
      if (!customFilename) {
        const contentDisposition = (response as any)?.headers?.get('Content-Disposition');
        if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }
      }
  
      // Set the download attributes and trigger the download.
      anchor.href = blobUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
  
      // Clean up the DOM and revoke the blob URL.
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      throw error;
    }
  }
  